import React, { PropsWithChildren } from 'react'

import { ReactFC } from 'common/types/ReactFC'
import { TPlanSummary } from 'common/cms-types'
import FeatureItem from 'components/FeatureItem'
import imageResize, { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'
import SubscriptionPricing from 'components/SubscriptionPricing'

const features = [
  {
    description:
      'Simply register it with Companies House as your registered office address. The plan also offers a director service address add-on, offering a prestigious business address while ensuring privacy and easy access to official correspondence.',
    title: 'A professional address.',
  },
  {
    description:
      'We scan and send your business mail to your inbox, mobile app, and customer portal within one business day.',
    title: 'Mail handling straight to your inbox within 1 day.',
  },
  {
    description:
      'Get business mail summaries, tags, and a fully searchable document hub — all in one place.',
    title: 'AI-powered mail services to streamline your business.',
  },
  {
    description:
      'Never lose a document again — over 1 million pieces of mail processed.',
    title: 'Digitally stored safely and securely.',
  },
  {
    description:
      'Use it as your official registered company address, keeping your personal address off the Companies House register while projecting a professional image.',
    title: 'Companies House compliance.',
  },
  {
    description: 'Simplicity for your mail and more.',
    title: 'One plan, 10-minute setup, monthly cancellation.',
  },
]

type FeaturesCardType = {
  className?: string
  planSummary: TPlanSummary
}

const FeaturesCard: ReactFC<PropsWithChildren<FeaturesCardType>> = ({
  className = '',
  planSummary,
}) => {
  const size = useWindowSize()
  return (
    <div className={`card`}>
      <div className='row pt-8 pt-lg-10 g-3 g-lg-4'>
        <div className='col-12 col-lg-6'>
          <div className='ps-4 ps-lg-20 pb-5 pb-lg-9 pe-4 pe-lg-0'>
            <h3 className='fs-fluid-24-32 fw-semibold mb-8'>
              Our{' '}
              <span className='color-pink2 fw-semibold'>
                virtual office package
              </span>{' '}
              includes:
            </h3>

            <div className='d-flex flex-column gap-4 fs-fluid-16-20'>
              {features.map((feature) => (
                <FeatureItem
                  key={feature.title}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-6 d-flex flex-column'>
          <div className='pe-4 pe-lg-20 ps-4 ps-lg-0 pb-5 pb-lg-0'>
            {!size.isLgOrHigher && (
              <img
                {...generateSrcSet(
                  '/images/new/robot-mail-preview-hello-hero.png',
                  500,
                  494,
                )}
                width={500}
                height={494}
                alt='HaiRo™ mail service with robot mascot and phone displaying mail interface'
                className='rounded-img w-100 h-auto object-fit-contain'
                loading='lazy'
              />
            )}
            <SubscriptionPricing planSummary={planSummary} />
          </div>
          {size.isLgOrHigher && (
            <div
              className='position-relative pointer-event-none h-100'
              style={{
                marginTop: -30,
              }}
            >
              <img
                {...generateSrcSet(
                  '/images/new/robot-mail-preview-cut.png',
                  659,
                  689,
                )}
                height={689}
                width={659}
                alt='HaiRo™ mail service with robot mascot and phone displaying mail interface'
                className='rounded-img h-auto max-h-100 w-100 position-absolute bottom-0 object-fit-contain object-position-right'
                loading='lazy'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FeaturesCard
